import { withTasksAccess } from "@crm-src/components/page-permission-guard"

import { useCallback, useMemo, useRef, useState, useEffect } from "react"
import { useSearchParams, useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import type { ChipProps } from "@mui/material"
import { PageHeader } from "@/components/ui/page-header"
import { PageLoader } from "@/components/ui/page-loader"
import { ConfirmDialog } from "@/components/ui/confirm-dialog"
import { Pagination } from "@/components/ui/pagination"
import { usePagedList } from "@/hooks/usePagedList"
import { useTasks } from "@crm-src/hooks/useTasks"
import { useUsers } from "@crm-src/hooks/useUsers"
import { useAuth } from "@crm-src/hooks/useAuth"
import { usePermissions } from "@crm-src/hooks/usePermissions"
import type { Task } from "@crm-src/types/task"
import { TaskCalendar } from "@crm-src/components/task-calendar"
import { AddTaskDialog } from "@crm-src/components/tasks/add-task-dialog"
import { EditTaskDialog } from "@crm-src/components/tasks/edit-task-dialog"
import { EditScopeDialog } from "@crm-src/components/tasks/edit-scope-dialog"
import { DeleteScopeDialog } from "@crm-src/components/tasks/delete-scope-dialog"
import { useTableQuery } from "@crm-src/components/table/useTableQuery"
import { useDebouncedSearch } from "@crm-src/components/table/useDebouncedSearch"
import { TableEmptyState } from "@crm-src/components/table/TableEmptyState"
import { TASKS_TABLE } from "@crm-src/components/table/configs"
import {
  ANY, TASKS_CLIENT_FACET_KEYS, TASK_SORT_OPTIONS,
  currentMonthRange,
  facetSelection, facetValues, matchesTaskFilters, tasksViewFrom, tasksViewHref,
  type TasksClientFacetKey,
} from "./tasks-page-query"
import {
  CalendarDays,
  Plus,
  Search,
  Clock,
  CheckCircle2,
  AlertCircle,
  CheckSquare,
  Trash2,
  Edit,
  Repeat,
  List,
  ArrowDownAZ,
  ArrowUpAZ,
} from "lucide-react"
import { format, parseISO } from "date-fns"

import { StatsCard } from "@crm-src/components/stats-card"
import { StatsCardsSkeleton } from "@crm-src/components/stats-cards-skeleton"
import api from "@crm-src/lib/api"

// Priority and status → MUI Chip palette color (inlined during the MUI 5
// migration from the former Tailwind class maps).
const priorityColors: Record<Task["priority"], ChipProps["color"]> = {
  low: "default",
  medium: "info",
  high: "warning",
  urgent: "error",
}

const statusColors: Record<Task["status"], ChipProps["color"]> = {
  pending: "warning",
  in_progress: "info",
  completed: "success",
  cancelled: "default",
}

function TasksPage() {
  const {
    tasks,
    calendarTasks,
    isLoading,
    error,
    stats,
    getTasks,
    getCalendarTasks,
    createTask,
    updateTask,
    deleteTask,
    cancelTaskOccurrence,
    toggleComplete,
    getTaskStats,
    clearTasksError,
    clearTasksCache
  } = useTasks()
  
  const {
    users,
    refresh: getUsers
  } = useUsers()
  
  const { user: currentUser } = useAuth()
  const { isAdmin } = usePermissions()
  const userIsAdmin = isAdmin()

  // Sort + filter state, held in the URL so a refresh, the back button and a
  // pasted link all reproduce the same view — the same contract the five CRM
  // TABLE pages follow. Tasks renders cards, so the sort is chosen from a
  // dropdown instead of a column header, but the state and the server keys are
  // the shared ones. See components/table/.
  //
  // `facetKeys` is annotated, not inferred: `TASKS_CLIENT_FACET_KEYS` is a
  // client-only list (the API declares no task facets — see
  // tasks-page-query.ts), and the annotation is what keeps `setFacet` refusing
  // a key this page never declared.
  const facetKeys = useMemo<readonly TasksClientFacetKey[]>(() => TASKS_CLIENT_FACET_KEYS, [])
  const table = useTableQuery({
    defaultSort: TASKS_TABLE.defaultSort,
    defaultDir: TASKS_TABLE.defaultDir,
    facetKeys,
  })

  // `as const` config entries carry no `align`, so the option list needs the
  // explicit element type rather than the literal one TypeScript infers.
  const sortOptions = useMemo(() => TASK_SORT_OPTIONS, [])
  const activeSortLabel =
    sortOptions.find((option) => option.key === table.sort)?.label ?? table.sort

  // Only the TEXT box debounces — a sort or filter change applies at once. The
  // shared hook also owns the "a new term means page 1 again" reset, which is
  // the part that loops if a page writes it inline. Search text is deliberately
  // NOT in the URL; sort and the two filters are.
  //
  // The page reset goes through a ref because Tasks paginates CLIENT-side:
  // `usePagedList` owns the page number, but it is built from `filteredTasks`,
  // which is built from this hook's committed term — passing `taskPaging.setPage`
  // directly would be a cycle. The indirection keeps the callback identity
  // stable, which is also what `useDebouncedSearch` needs.
  const resetPageRef = useRef<(page: number) => void>(() => {})
  const resetPage = useCallback((page: number) => resetPageRef.current(page), [])
  const search = useDebouncedSearch(resetPage)
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [taskToDelete, setTaskToDelete] = useState<Task | null>(null)
  const [taskToEdit, setTaskToEdit] = useState<Task | null>(null)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isDeleteScopeDialogOpen, setIsDeleteScopeDialogOpen] = useState(false)
  const [isEditScopeDialogOpen, setIsEditScopeDialogOpen] = useState(false)
  
  // Get view mode from URL params
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const viewMode = tasksViewFrom(searchParams)
  
  const [newTask, setNewTask] = useState({
    name: "",
    description: "",
    priority: "medium" as Task["priority"],
    due_date: (() => {
      const date = new Date();
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    })(),
    due_time: "09:00",
    assigned_to_id: undefined as number | undefined,
    is_recurring: false,
    recurrence_pattern: "daily" as "daily" | "weekly" | "monthly" | "yearly" | "custom",
    recurrence_interval: 1,
    recurrence_end_date: "",
    recurrence_count: undefined as number | undefined,
  })
  
  const [editTask, setEditTask] = useState({
    name: "",
    description: "",
    priority: "medium" as Task["priority"],
    due_date: "",
    due_time: "",
    assigned_to_id: undefined as number | undefined,
    status: "pending" as Task["status"],
    is_recurring: false,
    recurrence_pattern: "daily" as "daily" | "weekly" | "monthly" | "yearly" | "custom",
    recurrence_interval: 1,
    recurrence_end_date: "",
    recurrence_count: undefined as number | undefined,
  })

  // The list request. Only the SORT travels to the server — status, priority
  // and the search term stay client-side over the fetched page, which is this
  // page's long-standing model and is unchanged. `sort_by`/`sort_direction` is
  // the spelling `TaskFilters` carries; `Sortable` accepts it and the short
  // `sort`/`dir` pair the dashboard pulse card sends alike.
  const reloadTasks = useCallback(
    () => getTasks({
      include_stats: true,
      sort_by: table.sort,
      sort_direction: table.dir,
    }),
    [getTasks, table.sort, table.dir],
  )

  // Refetch whenever the chosen sort changes — the server, not the browser,
  // decides the order (and therefore which rows land on the page).
  useEffect(() => {
    reloadTasks()
  }, [table.sort, table.dir])

  // Load the user roster once; it feeds the assignee pickers, not the list.
  useEffect(() => {
    getUsers()
  }, [])

  // Note: Calendar view fetches its own data dynamically based on visible date range

  // Auto-assign to current user on initial load (but allow manual changes)
  useEffect(() => {
    if (currentUser && !newTask.assigned_to_id && newTask.name === "") {
      setNewTask(prev => ({
        ...prev,
        assigned_to_id: currentUser.id
      }))
    }
  }, [currentUser])

  // Auto-switch to list view once a search term COMMITS: a hit that only the
  // card list can show is invisible behind the calendar.
  useEffect(() => {
    if (search.search !== "" && viewMode === 'calendar') {
      navigate(tasksViewHref(searchParams, 'list'))
    }
  }, [search.search])

  // The two filter dropdowns read straight from the URL. `useTableQuery` models
  // every facet as a LIST (the table pages multi-select); these are
  // single-choice, so an empty list is the "All" sentinel.
  const filterStatus = facetSelection(table.facets.status)
  const filterPriority = facetSelection(table.facets.priority)

  // Filter tasks on client side — deliberately in memory, over the page the
  // server already sorted. The API declares no task facets.
  // Note: Backend handles role-based filtering (admins see all, non-admins see only their tasks)
  const filteredTasks = useMemo(
    () => tasks.filter((task) => matchesTaskFilters(task, {
      searchTerm: search.search, status: filterStatus, priority: filterPriority,
    })),
    [tasks, search.search, filterStatus, filterPriority],
  )

  // Paginate the list view at 25/page. The calendar view ignores
  // pagination — it spans a full month visually and already caps
  // density via day-cell limits.
  const taskPaging = usePagedList(filteredTasks, 25)

  // See `resetPage` above: the debounced search box sends the client-side
  // pager back to page 1, and it can only reach it from here.
  useEffect(() => { resetPageRef.current = taskPaging.setPage }, [taskPaging.setPage])

  const handleAddTask = async () => {
    try {
      // Create a proper Date object in local timezone and convert to ISO
      const localDate = new Date(`${newTask.due_date}T${newTask.due_time}:00`)
      const dueDateTime = localDate.toISOString()
      
      await createTask({
        name: newTask.name,
        description: newTask.description,
        priority: newTask.priority,
        due_date: dueDateTime,
        assigned_to_id: newTask.assigned_to_id || undefined,
        is_recurring: newTask.is_recurring,
        recurrence_pattern: newTask.is_recurring ? newTask.recurrence_pattern : undefined,
        recurrence_interval: newTask.is_recurring ? newTask.recurrence_interval : undefined,
        recurrence_end_date: newTask.is_recurring && newTask.recurrence_end_date ? 
          new Date(`${newTask.recurrence_end_date}T23:59:59`).toISOString() : undefined,
        recurrence_count: newTask.is_recurring ? newTask.recurrence_count : undefined,
      })

      // Notify calendar about new task
      window.dispatchEvent(new CustomEvent('taskCreated'))

      setNewTask({
        name: "",
        description: "",
        priority: "medium",
        due_date: new Date().toISOString().split('T')[0] ?? "",
        due_time: "09:00",
        assigned_to_id: currentUser ? currentUser.id : undefined,
        is_recurring: false,
        recurrence_pattern: "daily",
        recurrence_interval: 1,
        recurrence_end_date: "",
        recurrence_count: undefined,
      })
      setIsAddDialogOpen(false)
      
      // Stats are included in the list response; `reloadTasks` keeps the
      // active sort so a refresh does not silently reorder the list.
      await reloadTasks()
      
      // If in calendar view, refresh calendar data too
      if (viewMode === 'calendar') {
        await getCalendarTasks(currentMonthRange())
      }
    } catch (error) {
      console.error('Failed to create task:', error)
    }
  }

  const handleToggleComplete = async (taskId: string) => {
    try {
      const isRecurringInstance = taskId.includes('_')
      
      // Toggle completion - reducer will update the task state
      await toggleComplete(taskId)
      
      // For recurring instances, refresh after a short delay to ensure backend has processed
      // For regular tasks, the reducer update should be sufficient
      if (isRecurringInstance) {
        // Small delay to ensure backend has processed the exception deletion/update
        await new Promise(resolve => setTimeout(resolve, 200))
        // Refresh tasks list to get updated status from backend
        // Stats are included in the list response; `reloadTasks` keeps the
        // active sort so a refresh does not silently reorder the list.
        await reloadTasks()
      }
      
      // Notify calendar about task completion status change
      window.dispatchEvent(new CustomEvent('taskCompleted', { 
        detail: { taskId }
      }))
      
      // If in calendar view, refresh calendar data too
      if (viewMode === 'calendar') {
        await getCalendarTasks(currentMonthRange())
      }
    } catch (error) {
      console.error('Failed to toggle task completion:', error)
    }
  }

  const handleDeleteTask = async () => {
    if (!taskToDelete) return

    // For recurring instances, the scope dialog handles the delete
    // This function is now only called for non-recurring tasks or parent recurring tasks
    await performTaskDelete('all')
  }

  const performTaskDelete = async (scope: 'this' | 'all') => {
    if (!taskToDelete) return

    try {
      if (scope === 'this' && taskToDelete.is_recurring_instance && taskToDelete.original_date) {
        // Cancel only this specific occurrence
        const parentTaskId = taskToDelete.parent_task_id || taskToDelete.id
        await cancelTaskOccurrence(parentTaskId, taskToDelete.original_date)
      } else {
        // Delete the entire task/series
        // For recurring instances with 'all' scope, delete the parent task
        const taskId = taskToDelete.is_recurring_instance && taskToDelete.parent_task_id
          ? taskToDelete.parent_task_id
          : taskToDelete.id
        await deleteTask(taskId)
      }

      setTaskToDelete(null)
      setIsDeleteDialogOpen(false)
      setIsDeleteScopeDialogOpen(false)

      // Stats are included in the list response; `reloadTasks` keeps the
      // active sort so a refresh does not silently reorder the list.
      await reloadTasks()

      // Notify other components (like calendar) that tasks have changed
      window.dispatchEvent(new CustomEvent('taskDeleted', {
        detail: { taskId: taskToDelete.id }
      }))

      // If in calendar view, refresh calendar data too
      if (viewMode === 'calendar') {
        await getCalendarTasks(currentMonthRange())
      }
    } catch (error) {
      console.error('Failed to delete task:', error)
      throw error
    }
  }

  const handleDateClick = (date: Date) => {
    // Set the clicked date in the new task form
    setNewTask(prev => ({
      ...prev,
      due_date: format(date, 'yyyy-MM-dd'),
      due_time: "09:00"
    }))
    
    // Open the add task dialog
    setIsAddDialogOpen(true)
  }

  const handleEditTask = (task: Task) => {
    const dueDate = parseISO(task.due_date)
    
    // For recurring instances, we need to preserve the recurring pattern
    // The instance itself has is_recurring: false, but it belongs to a recurring series
    const isPartOfRecurringSeries = task.is_recurring_instance || task.is_recurring
    
    setEditTask({
      name: task.name,
      description: task.description || "",
      priority: task.priority,
      due_date: format(dueDate, 'yyyy-MM-dd'),
      due_time: format(dueDate, 'HH:mm'),
      assigned_to_id: task.assigned_to?.id,
      status: task.status,
      is_recurring: isPartOfRecurringSeries,
      recurrence_pattern: task.recurring_pattern?.recurring_type || "daily",
      recurrence_interval: task.recurring_pattern?.repeat_interval || 1,
      recurrence_end_date: task.recurring_pattern?.ending_at ? 
        format(parseISO(task.recurring_pattern.ending_at), 'yyyy-MM-dd') : "",
      recurrence_count: task.recurring_pattern?.max_occurrences,
    })
    setTaskToEdit(task)
    setIsEditDialogOpen(true)
  }

  const handleUpdateTask = async () => {
    if (!taskToEdit) return
    
    // If this is a recurring instance, show the scope dialog
    if (taskToEdit.is_recurring_instance) {
      setIsEditScopeDialogOpen(true)
      return
    }
    
    // Otherwise, proceed with normal update
    await performTaskUpdate('all')
  }

  const performTaskUpdate = async (scope: 'this' | 'all') => {
    if (!taskToEdit) return
    
    try {
      // Create a proper Date object in local timezone and convert to ISO
      const localDate = new Date(`${editTask.due_date}T${editTask.due_time}:00`)
      const dueDateTime = localDate.toISOString()
      
      if (scope === 'this' && taskToEdit.is_recurring_instance && taskToEdit.original_date) {
        // Create a modification exception for this occurrence only
        const parentTaskId = taskToEdit.parent_task_id || taskToEdit.id
        await api.post(`/tasks/${parentTaskId}/occurrences/${taskToEdit.original_date}/modify`, {
          modifications: {
            name: editTask.name,
            description: editTask.description,
            priority: editTask.priority,
            status: editTask.status,
            due_date: dueDateTime,
            assigned_to_id: editTask.assigned_to_id,
          }
        })
      } else {
        // Update the parent task (affects all occurrences)
        const taskId = taskToEdit.is_recurring_instance && taskToEdit.parent_task_id 
          ? taskToEdit.parent_task_id 
          : taskToEdit.id
        
        
        // Build update data for all occurrences
        const updateData: any = {
          name: editTask.name,
          description: editTask.description,
          priority: editTask.priority,
          status: editTask.status,
          assigned_to_id: editTask.assigned_to_id,
          is_recurring: editTask.is_recurring,
          due_date: dueDateTime, // ALWAYS update due_date to change the pattern's starting_at
          recurrence_pattern: editTask.is_recurring ? editTask.recurrence_pattern : undefined,
          recurrence_interval: editTask.is_recurring ? editTask.recurrence_interval : undefined,
          recurrence_end_date: editTask.is_recurring && editTask.recurrence_end_date ?
            new Date(`${editTask.recurrence_end_date}T23:59:59`).toISOString() : undefined,
          recurrence_count: editTask.is_recurring && editTask.recurrence_count ? 
            editTask.recurrence_count : undefined,
        }
        
        
        await updateTask(taskId, updateData)
        
      }
      
      setIsEditDialogOpen(false)
      setIsEditScopeDialogOpen(false)
      setTaskToEdit(null)
      
      // Small delay to ensure backend has processed the update
      await new Promise(resolve => setTimeout(resolve, 100))
      
      // Clear caches and reload data
      clearTasksCache()
      
      // Stats are included in the list response; `reloadTasks` keeps the
      // active sort so a refresh does not silently reorder the list.
      await reloadTasks()
      
      // Notify calendar about task update (calendar will refresh itself with correct date range)
      window.dispatchEvent(new CustomEvent('taskUpdated'))
      
    } catch (error) {
      console.error('Failed to update task:', error)
    }
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Tasks"
        description="Manage and track your tasks"
        actions={
          <>
            <Button
              variant="outlined"
              color="inherit"
              component={Link}
              /* Keeps the query string: sort, dir, status and priority are all URL
                 state now, so a bare `?view=` would reset the list on every
                 toggle. Pinned by tasks-page-query.test.ts. */
              to={tasksViewHref(searchParams, viewMode === "calendar" ? "list" : "calendar")}
              startIcon={viewMode === "calendar" ? <List size={16} /> : <CalendarDays size={16} />}
            >
              {viewMode === "calendar" ? "List View" : "Calendar View"}
            </Button>
            <Button variant="contained" startIcon={<Plus size={16} />} onClick={() => setIsAddDialogOpen(true)}>
              Add Task
            </Button>
          </>
        }
      />

      {/* Stats Cards */}
      {!isLoading && stats ? (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" }, gap: 2 }}>
          <StatsCard
            title="Total Tasks"
            value={stats.total}
            icon={CheckSquare}
            iconColor="text-blue-600"
          />
          <StatsCard
            title="Completed"
            value={stats.completed}
            icon={CheckCircle2}
            iconColor="text-green-600"
            valueColor="text-green-600"
          />
          <StatsCard
            title="In Progress"
            value={stats.in_progress}
            icon={Clock}
            iconColor="text-blue-600"
            valueColor="text-blue-600"
          />
          <StatsCard
            title="Overdue"
            value={stats.overdue}
            icon={AlertCircle}
            iconColor="text-red-600"
            valueColor="text-red-600"
          />
        </Box>
      ) : (
        <StatsCardsSkeleton />
      )}

      <Card>
        <CardHeader
          title={
            <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2, alignItems: { sm: "center" }, justifyContent: { sm: "space-between" } }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
                <Box sx={{ display: "flex", color: "text.secondary" }}><Search size={16} /></Box>
                <TextField
                  size="small"
                  placeholder="Search tasks..."
                  value={search.input}
                  onChange={(e) => search.setInput(e.target.value)}
                  sx={{ maxWidth: "24rem", width: "100%" }}
                  {...(search.isSearching ? {
                    InputProps: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>Searching...</Typography>
                        </InputAdornment>
                      ),
                    },
                  } : {})}
                />
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                {/*
                  There are no column headers to click on a card list, so the
                  server sort keys are offered here instead — inline with the
                  filters, as one row of list controls. `toggleSort` is the same
                  shared reducer `SortableHeader` drives on the table pages:
                  naming a DIFFERENT column selects it ascending, naming the
                  ACTIVE one flips the direction, which is exactly what the
                  arrow button next to it needs.
                */}
                <TextField
                  select
                  size="small"
                  id="tasks-sort"
                  label="Sort by"
                  value={table.sort}
                  onChange={(e) => table.toggleSort(e.target.value)}
                  sx={{ minWidth: 160 }}
                  inputProps={{ "aria-label": "Sort by" }}
                >
                  {sortOptions.map((option) => (
                    <MenuItem key={option.key} value={option.key}>{option.label}</MenuItem>
                  ))}
                </TextField>

                <Tooltip
                  title={table.dir === "asc"
                    ? `${activeSortLabel}: ascending — click for descending`
                    : `${activeSortLabel}: descending — click for ascending`}
                >
                  <IconButton
                    size="small"
                    aria-label={table.dir === "asc" ? "Sort descending" : "Sort ascending"}
                    onClick={() => table.toggleSort(table.sort)}
                  >
                    {table.dir === "asc" ? <ArrowUpAZ size={16} /> : <ArrowDownAZ size={16} />}
                  </IconButton>
                </Tooltip>

                <TextField
                  select
                  size="small"
                  id="tasks-status-filter"
                  value={filterStatus}
                  onChange={(e) => table.setFacet("status", facetValues(e.target.value))}
                  sx={{ width: 140 }}
                  inputProps={{ "aria-label": "Filter by status" }}
                >
                  <MenuItem value={ANY}>All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </TextField>

                <TextField
                  select
                  size="small"
                  id="tasks-priority-filter"
                  value={filterPriority}
                  onChange={(e) => table.setFacet("priority", facetValues(e.target.value))}
                  sx={{ width: 140 }}
                  inputProps={{ "aria-label": "Filter by priority" }}
                >
                  <MenuItem value={ANY}>All Priority</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </TextField>

                {(table.activeFilterCount > 0 || search.input !== "") && (
                  <Button
                    variant="outlined"
                    color="inherit"
                    size="small"
                    onClick={() => {
                      table.clearFacets()
                      search.clear()
                    }}
                  >
                    Clear Filters
                  </Button>
                )}
              </Box>
            </Box>
          }
        />

        <CardContent>
          {viewMode === "list" ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {isLoading ? (
                <PageLoader />
              ) : filteredTasks.length === 0 ? (
                <Box sx={{ textAlign: "center" }}>
                  {/*
                    The shared empty state, because now that status and priority
                    live in the URL a visitor can arrive at an already-narrowed
                    list. "No tasks — get started by creating one" would then be
                    a lie, and the way out (Clear filters) has to be offered
                    where the missing rows would have been.
                  */}
                  <TableEmptyState
                    entityNoun="tasks"
                    hasSearch={search.search !== ""}
                    activeFilterCount={table.activeFilterCount}
                    onClearFilters={table.clearFacets}
                    emptyTitle="No tasks"
                    emptyBody="Get started by creating a new task."
                  />
                  {search.search === "" && table.activeFilterCount === 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Button variant="contained" startIcon={<Plus size={16} />} onClick={() => setIsAddDialogOpen(true)}>
                        Add Task
                      </Button>
                    </Box>
                  )}
                </Box>
              ) : (
                taskPaging.paged.map((task) => {
                  const isOverdue = task.overdue

                  return (
                    <Card
                      key={task.id}
                      sx={{
                        transition: "all 0.2s",
                        "&:hover": { boxShadow: 3 },
                        ...(isOverdue && task.status !== "completed" ? { borderLeft: 4, borderColor: "error.main" } : {}),
                      }}
                    >
                      <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
                        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", px: 2 }}>
                          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, flex: 1 }}>
                            <Checkbox
                              checked={task.status === "completed"}
                              onChange={() => handleToggleComplete(task.id)}
                              sx={{ mt: 0.5 }}
                            />
                            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography
                                  component="h3"
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: "1rem",
                                    ...(task.status === "completed" ? { textDecoration: "line-through", color: "text.secondary" } : {}),
                                  }}
                                >
                                  {task.name}
                                </Typography>
                                {(task.is_recurring || task.is_recurring_instance) && (
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    icon={<Repeat size={12} />}
                                    label={task.is_recurring_instance ? 'Recurring Instance' : 'Recurring'}
                                  />
                                )}
                              </Box>
                              {task.description && (
                                <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>{task.description}</Typography>
                              )}
                              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mt: 1 }}>
                                <Chip size="small" color={priorityColors[task.priority] ?? "default"} label={task.priority} />
                                <Chip size="small" color={statusColors[task.status] ?? "default"} label={task.status.replace('_', ' ')} />
                                {task.assigned_to && (
                                  <Typography component="span" sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                                    Assigned to: {task.assigned_to.full_name}
                                  </Typography>
                                )}
                                <Typography
                                  component="span"
                                  sx={{
                                    fontSize: "0.75rem",
                                    ...(isOverdue && task.status !== "completed" ? { color: "error.main", fontWeight: 600 } : { color: "text.secondary" }),
                                  }}
                                >
                                  Due: {format(parseISO(task.due_date), 'MMM d, yyyy h:mm a')}
                                </Typography>
                                {/*
                                  "Created" is one of the sort options, and a
                                  sort has to order by something the card
                                  actually shows — otherwise picking it looks
                                  like it shuffled the list at random.
                                */}
                                {task.created_at && (
                                  <Typography component="span" sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                                    Created: {format(parseISO(task.created_at), 'MMM d, yyyy')}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Button
                              variant="text"
                              color="inherit"
                              size="small"
                              aria-label="Edit task"
                              sx={{ minWidth: 0 }}
                              onClick={() => handleEditTask(task)}
                            >
                              <Edit size={16} />
                            </Button>
                            <Button
                              variant="text"
                              color="inherit"
                              size="small"
                              aria-label="Delete task"
                              sx={{ minWidth: 0 }}
                              onClick={() => {
                                setTaskToDelete(task)
                                // Show scope dialog for recurring instances
                                if (task.is_recurring_instance) {
                                  setIsDeleteScopeDialogOpen(true)
                                } else {
                                  setIsDeleteDialogOpen(true)
                                }
                              }}
                            >
                              <Trash2 size={16} color="#dc2626" />
                            </Button>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  )
                })
              )}
              <Pagination
                page={taskPaging.page}
                perPage={taskPaging.perPage}
                totalCount={taskPaging.totalCount}
                totalPages={taskPaging.totalPages}
                entityNoun="tasks"
                hideIfSinglePage
                onPageChange={taskPaging.setPage}
                onPerPageChange={taskPaging.setPerPage}
              />
            </Box>
          ) : (
            <TaskCalendar
              onTaskClick={handleEditTask}
              onDateClick={handleDateClick}
              filteredTasks={filteredTasks}
              filterStatus={filterStatus}
              filterPriority={filterPriority}
              searchTerm={search.search}
            />
          )}
        </CardContent>
      </Card>

      <AddTaskDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        newTask={newTask}
        setNewTask={setNewTask}
        users={users}
        userIsAdmin={userIsAdmin}
        onSubmit={handleAddTask}
      />

      <EditTaskDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        taskToEdit={taskToEdit}
        editTask={editTask}
        setEditTask={setEditTask}
        users={users}
        userIsAdmin={userIsAdmin}
        onSubmit={handleUpdateTask}
        onDelete={() => {
          if (taskToEdit) {
            setTaskToDelete(taskToEdit)
            setIsEditDialogOpen(false)
            // Show scope dialog for recurring instances
            if (taskToEdit.is_recurring_instance) {
              setIsDeleteScopeDialogOpen(true)
            } else {
              setIsDeleteDialogOpen(true)
            }
          }
        }}
      />

      {/* `tasks#destroy` calls `Task#soft_delete!` (`deleted_at`), and every
          tasks_controller read is scoped `.active` (`deleted_at: nil`) — so the
          task leaves the lists and stays readable in the database. Its
          recurring_pattern and recurring_exceptions are not cascaded.
          See docs/crm.md → "Delete is a soft delete". */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        title={taskToDelete?.is_recurring_instance ? 'Cancel Occurrence' : 'Delete Task'}
        description={
          taskToDelete?.is_recurring_instance ? (
            <>
              Are you sure you want to cancel this occurrence of "{taskToDelete?.name}" on{' '}
              {taskToDelete?.original_date && format(parseISO(taskToDelete.original_date), 'MMM d, yyyy')}?
              <Box component="span" sx={{ display: "block", mt: 1, color: "info.main", fontWeight: 600 }}>
                Note: This will only cancel this specific occurrence. The recurring series will continue for other dates.
              </Box>
            </>
          ) : (
            <>
              Are you sure you want to delete "{taskToDelete?.name}"? It is removed from the task
              lists and the calendar. The record is kept, so an administrator can restore it.
              {taskToDelete?.is_recurring && (
                <Box component="span" sx={{ display: "block", mt: 1, color: "warning.main", fontWeight: 600 }}>
                  Warning: This is a recurring task. Deleting it takes the entire series and all
                  future occurrences off the lists.
                </Box>
              )}
            </>
          )
        }
        confirmLabel={taskToDelete?.is_recurring_instance ? 'Cancel Occurrence' : 'Delete Task'}
        onConfirm={handleDeleteTask}
      />

      <EditScopeDialog
        open={isEditScopeDialogOpen}
        onOpenChange={setIsEditScopeDialogOpen}
        taskToEdit={taskToEdit}
        onScopeSelect={performTaskUpdate}
      />

      <DeleteScopeDialog
        open={isDeleteScopeDialogOpen}
        onOpenChange={setIsDeleteScopeDialogOpen}
        onScopeSelect={performTaskDelete}
      />
    </Box>
  )
}

export default withTasksAccess(TasksPage)
